import React, { useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
    Theme,
    Typography,
    List,
    Link,
    Breadcrumbs,
    TextField,
} from '@mui/material';

import Page from 'components/Page';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
        },
        top: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            margin: theme.spacing(1),
        },
        content: {
            display: 'grid',
            [theme.breakpoints.down('md')]: {
                gridTemplateRows: 'auto 1fr',
            },
            [theme.breakpoints.up('md')]: {
                gridTemplateColumns: 'auto 1fr',
            },
            gap: theme.spacing(1),
        },
        section: {
            backgroundColor: theme.palette.common.neutralXXLight,
            margin: theme.spacing(1),
            padding: theme.spacing(1),
            minWidth: 300,
        },
        search: {
            width: '100%',
        },
    }),
);

interface ListDetailsPageProps {
    list?: React.ReactNode[];
    title?: string;
    breadcrumbs?: {
        text: string;
        link?: string;
    }[];
    onSearchChange?: (searchText: string) => void;
    children?: React.ReactNode;
}

const ListDetailsPage: React.FunctionComponent<ListDetailsPageProps> = ({
    list,
    children,
    title,
    breadcrumbs,
    onSearchChange,
}) => {
    const classes = useStyles();

    const [search, setSearch] = useState('');

    const brdcrumbs = breadcrumbs?.map((b) => {
        if (b.link)
            return (
                <Link
                    key={`${b.text}-${b.link}`}
                    color="inherit"
                    component={RouterLink}
                    to={b.link}
                >
                    {b.text}
                </Link>
            );

        return (
            <Typography key={b.text} color="textPrimary">
                {b.text}
            </Typography>
        );
    });

    return (
        <Page className={classes.root}>
            <div className={classes.top}>
                <Breadcrumbs>{brdcrumbs}</Breadcrumbs>
                {title && (
                    <Typography variant="h5" color="textPrimary">
                        {title}
                    </Typography>
                )}
            </div>
            <div className={classes.content}>
                <div className={classes.section}>
                    {onSearchChange && (
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            className={classes.search}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                onSearchChange(e.target.value);
                            }}
                        />
                    )}
                    <List>{list}</List>
                    {list && list.length === 0 && (
                        <Typography variant="subtitle1" align="center">
                            0 results found
                        </Typography>
                    )}
                </div>
                <div className={classes.section}>{children}</div>
            </div>
        </Page>
    );
};

ListDetailsPage.displayName = 'ListDetailsPage';
export default ListDetailsPage;
